// src/features/procedures/components/steps/MultipleChoiceStep.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  FormHelperText,
} from '@mui/material';

/**
 * Multiple Choice Step Component
 * 
 * Options Config:
 * {
 *   "choices": [                              // Array of choices
 *     "Fremragende - Ingen problemer",
 *     "God - Mindre slitage",
 *     "Acceptabel - Nogle bekymringer"
 *   ],
 *   "allow_multiple": false,                  // Allow multiple selections (checkbox style)
 *   "min_selections": 1,                      // Minimum selections (for multiple choice)
 *   "max_selections": 3,                      // Maximum selections (for multiple choice)
 *   "default_value": "God - Mindre slitage"  // Default selected value
 * }
 */
export default function MultipleChoiceStep({ step, value, onChange, disabled, preview }) {
  const { t } = useTranslation(['procedures']);
  const options = step.options || {};
  const choices = options.choices || [];
  const allowMultiple = options.allow_multiple || false;
  const minSelections = options.min_selections || (allowMultiple ? 0 : 1);
  const maxSelections = options.max_selections || choices.length;

  const handleSingleChange = (event) => {
    onChange(event.target.value);
  };

  const handleMultipleChange = (choiceValue) => {
    const currentValues = Array.isArray(value) ? value : [];
    const isSelected = currentValues.includes(choiceValue);

    let newValues;
    if (isSelected) {
      newValues = currentValues.filter(v => v !== choiceValue);
    } else {
      if (currentValues.length >= maxSelections) {
        return; // Don't allow more selections
      }
      newValues = [...currentValues, choiceValue];
    }

    onChange(newValues);
  };

  if (allowMultiple) {
    const selectedCount = Array.isArray(value) ? value.length : 0;

    return (
      <FormControl component="fieldset" disabled={disabled || preview} fullWidth>
        {!preview && <FormLabel component="legend">{step.label}</FormLabel>}
        {!preview && step.description && (
          <FormHelperText sx={{ whiteSpace: 'pre-wrap' }}>{step.description}</FormHelperText>
        )}
        <FormGroup sx={{ mt: preview ? 0 : 1 }}>
          {choices.map((choice, index) => (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  checked={Array.isArray(value) && value.includes(choice)}
                  onChange={() => handleMultipleChange(choice)}
                  disabled={
                    disabled ||
                    preview ||
                    (!value?.includes(choice) && selectedCount >= maxSelections)
                  }
                />
              }
              label={choice}
            />
          ))}
        </FormGroup>
        <FormHelperText>
          {t('procedures:step_options.selection_required', { count: minSelections === maxSelections ? minSelections : `${minSelections}-${maxSelections}` })}
        </FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl component="fieldset" disabled={disabled || preview} fullWidth>
      <RadioGroup
        value={value || options.default_value || ''}
        onChange={handleSingleChange}
        sx={{ mt: preview ? 0 : 0.5 }}
      >
        {choices.map((choice, index) => (
          <FormControlLabel
            key={index}
            value={choice}
            slotProps={{ typography: { variant: 'body2' } }}
            control={<Radio />}
            label={choice}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
}